import { IMPORT_PROJECT_CRAWLER_LOADING } from '@components/App/Modal/constants'
import { isValidIDNUrl } from '@lib/utils/strings'
import { PERSONAL_WORKSPACE_ID } from '@store/reducers/dashboard/constants'
import { parseNestError } from '@store/reducers/dashboard/utils'
import {
  LimitReachedActions,
  PROJECTS_LIMIT_REACHED,
} from '@store/reducers/limitReached'
import {
  setActiveCrawlerJob,
  setFinishedCrawlerJob,
} from '@storeOld/reducers/crawler'
import { ErrorNotification } from '@storeOld/reducers/notification'
import { closeModal, openModal, openSystemDialog } from '@storeOld/reducers/ui'
import { useState } from 'react'
import { useDispatch } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useAuth } from '@/hooks'
import { NestClient } from '@/nest'
import { crawlerClient } from '@/services/crawler'
import type { PageNameSource } from '@/types'
import { NEW_CRAWLING } from '../../../SystemDialogs/constants'
import { prepareUrl } from '../components/Tabs/utils'

type Params = {
  teamId?: string
  folderId?: string | null
  intoOpenProject?: boolean
  metaTag: boolean
  // Infer page blocks and wireframes from the markup (OCT-4990).
  blocks: boolean
  // Also ask the crawler for the blocks' text content; needs `blocks`.
  blocksContent: boolean
  excludedUrls: string[]
  // What the imported pages are named after; a report has no page names.
  pageNames?: PageNameSource
  // Ingest the crawl as a sitemap report instead of a visual project.
  asReport: boolean
}

type StartCrawl = {
  loading: boolean
  inputError: boolean
  startCrawl: (url: string) => Promise<void>
}

const getImportGtmEvent = ({
  metaTag,
  blocks,
}: Pick<Params, 'metaTag' | 'blocks'>): GtmTriggerName => {
  if (blocks) {
    return GtmTriggerName.CRAWL_IMPORT_BLOCKS
  }
  if (metaTag) {
    return GtmTriggerName.CRAWL_IMPORT_SEO
  }
  return GtmTriggerName.CRAWL_IMPORT_PAGES
}

// Validates the url, warns about a crawl already running, then hands over to
// the crawler loading modal. Shared by the Import crawler tab and the
// standalone sitemap audit modal.
const useStartCrawl = ({
  teamId,
  folderId,
  intoOpenProject = false,
  metaTag,
  blocks,
  blocksContent,
  excludedUrls,
  pageNames = 'url',
  asReport,
}: Params): StartCrawl => {
  const dispatch = useDispatch()
  const { id: userId } = useAuth()
  const [inputError, setInputError] = useState(false)
  const [loading, setLoading] = useState(false)

  const canCreateProject = async (): Promise<boolean> => {
    try {
      return await NestClient.projects.canCreate(
        teamId ?? PERSONAL_WORKSPACE_ID
      )
    } catch (error) {
      const message = parseNestError(error)

      dispatch(
        message === PROJECTS_LIMIT_REACHED
          ? LimitReachedActions.alert()
          : ErrorNotification(message)
      )

      return false
    }
  }

  const confirmNewCrawling = (): Promise<boolean> =>
    new Promise((resolve) => {
      dispatch(
        openSystemDialog(NEW_CRAWLING, {
          callback: resolve,
        })
      )
    })

  const startCrawl = async (url: string) => {
    const preparedUrl = prepareUrl(url)

    if (!isValidIDNUrl(preparedUrl)) {
      setInputError(true)
      return
    }

    setLoading(true)
    setInputError(false)

    // The crawl ends in a new project (a report takes a slot too), so ask
    // about the plan's project limit before spending minutes on a crawl the
    // save would refuse. Same gate as the dashboard's New project button.
    if (userId && !intoOpenProject) {
      const allowed = await canCreateProject()
      if (!allowed) {
        setLoading(false)
        return
      }
    }

    if (userId) {
      const hasJobs = await crawlerClient.hasJobs(userId)
      if (hasJobs) {
        const confirmed = await confirmNewCrawling()
        if (!confirmed) {
          setLoading(false)
          return
        }
      }
      // @todo implement action
      // @ts-expect-error
      dispatch(setActiveCrawlerJob(null))
      // @todo implement action
      // @ts-expect-error
      dispatch(setFinishedCrawlerJob(null))
    }

    const modalDialog = document.querySelector<HTMLElement>('.Modal-dialog')
    const initialFrameSize = modalDialog
      ? { width: modalDialog.offsetWidth, height: modalDialog.offsetHeight }
      : undefined

    dispatch(closeModal())

    sendGtmEvent(
      asReport
        ? GtmTriggerName.REPORT_CREATE_STARTED
        : getImportGtmEvent({ metaTag, blocks }),
      'Click on Crawl button in crawler modal'
    )

    dispatch(
      // @ts-expect-error
      openModal(IMPORT_PROJECT_CRAWLER_LOADING, {
        isLocked: true,
        website: preparedUrl,
        originalWebsite: url,
        metaTag,
        blocks,
        blocksContent: blocks && blocksContent,
        excludedUrls,
        pageNames,
        teamId,
        folderId,
        intoOpenProject,
        asReport,
        initialFrameSize,
      })
    )
  }

  return { loading, inputError, startCrawl }
}

export default useStartCrawl
